var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { t } from 'i18next';
import { fetchEligiblePaymentMethods, getExternalPaymentSystemProps, getExternalPaymentUrl, isNonTokenizableOnly, isSkipPayment, } from '@business/utils/payment';
import { createNextAction, processNextActionRedirect, } from '@business/utils/payment-next-action';
import { useCrossWindowMessage } from '@data/hooks/useCrossWindowMessage';
import { useModal } from '@molecules/BEOffScreen';
import ModalExternalPayment from '@view/components/Modals/ExternalPayment/ModalExternalPayment';
import { ReservationService } from '@data/api';
import { useStore } from '@data/other/withStore';
import { scrollToTop } from '@view/utils/scroll-to-top';
import { eventSystem } from '@business/event-system';
import { ModalPaymentInProgress } from '@view/components/Modals/ExternalPayment/ModalPaymentInProgress';
import { FeatureFlag, useFeatureFlag } from '@business/feature-flags';
import { system } from '@config/system';
import { useAppParams } from './useAppParams';
import { useEffectOnce } from './useEffectOnce';
import { usePageNavigation } from './usePageNavigation';
// A message `source` is typed `MessageEventSource = WindowProxy | MessagePort |
// ServiceWorker`. Only a script-opened WindowProxy can (and should) be closed:
// calling `close()` on a MessagePort would tear down the channel, and closing
// the current window is never intended. Narrow the source to a closable pop-up
// Window, returning null for anything else (BENG-4091).
const asClosableWindow = (source) => {
    if (!source || source === window)
        return null;
    if (typeof MessagePort !== 'undefined' && source instanceof MessagePort) {
        return null;
    }
    if (typeof ServiceWorker !== 'undefined' && source instanceof ServiceWorker) {
        return null;
    }
    return source;
};
// Hosted: `useExternalPaymentPopup` alone can enable the flow.
// Immersive: `useExternalPaymentImmersiveExperience` must also be on in LaunchDarkly,
// or `isUsingExternalPaymentSystem` stays false and checkout uses the in-app Payment route.
// Uses `useFeatureFlag` so the result is reactive — toggling the immersive-experience
// flag re-renders consumer hooks, unlike reading FeatureFlagManager.isEnabled directly.
const useIsEnvironmentEligibleForExternalPaymentSystem = () => {
    const isImmersiveExperienceEnabled = useFeatureFlag(FeatureFlag.useExternalPaymentImmersiveExperience);
    return system.isHostedExperience || isImmersiveExperienceEnabled;
};
// `null` (unresolved or /settings failed) is not inline — the popup/modal stays.
const isResolvedNonTokenizableOnly = (eligibleMethods) => eligibleMethods !== null && isNonTokenizableOnly(eligibleMethods);
export const useIsExternalPaymentSystemEnabled = () => {
    const isEligible = useIsEnvironmentEligibleForExternalPaymentSystem();
    // Presentation-only override: the flow stays gated by `useExternalPaymentPopup`
    // exactly as the pop-up is, and this only swaps the pop-up for the modal
    // (iframe). The pop-up needs a real `window.open`, which the integration suite
    // cannot drive, so the modal is the only shape it can exercise the flow in.
    const isExternalPaymentModalForced = !!system.externalPaymentModal;
    const isUsingExternalPaymentPopup = useFeatureFlag(FeatureFlag.useExternalPaymentPopup);
    const isInlineNonTokenizableEnabled = useFeatureFlag(FeatureFlag.inlineNonTokenizablePayments);
    const { property: { payment }, } = useStore();
    // The popup/modal is the flow whenever it's enabled for a collected payment.
    const isExternalPaymentSystemEligible = isEligible && !isSkipPayment() && isUsingExternalPaymentPopup;
    // Keep checkout inline only once the resolved eligible methods are exclusively
    // non-tokenizable (or none) and the inline flag is on; popup/modal until then.
    const isNonTokenizablePaymentOnly = isExternalPaymentSystemEligible &&
        isInlineNonTokenizableEnabled &&
        isResolvedNonTokenizableOnly(payment.eligiblePaymentMethods);
    return {
        externalPaymentSystemMode: (isExternalPaymentModalForced
            ? 'modal'
            : 'popup'),
        isExternalPaymentSystemEligible,
        isInlineNonTokenizableEnabled,
        isNonTokenizablePaymentOnly,
        isPaymentEligibilityLoading: payment.isResolvingPaymentRouting,
        isUsingExternalPaymentSystem: isExternalPaymentSystemEligible && !isNonTokenizablePaymentOnly,
    };
};
export const getPopupWindowFeatures = () => {
    const width = 960;
    const height = 600;
    return {
        height,
        left: window.outerWidth / 2 + window.screenX - width / 2,
        top: window.outerHeight / 2 + window.screenY - height / 2,
        width,
    };
};
export const useExternalPaymentSystem = ({ onCompleteReservationProcess, onPaymentError, } = {}) => {
    const appParams = useAppParams();
    const navigate = usePageNavigation();
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const popupWindow = useRef(null);
    // Cached so the reservation is created only once across pop-up reopen (BENG-3933).
    const reservationResultRef = useRef(null);
    const isCreatingReservationRef = useRef(false);
    const awaitingThreeDsRef = useRef(false);
    const pendingRedirectNextActionRef = useRef(null);
    // Guards against re-entrancy (e.g. a double-click on Continue) so the routing
    // decision can't fire two /settings requests or two proceed actions.
    const isCompletingCheckoutStepRef = useRef(false);
    // Set once a terminal outcome is reached so the pop-up must close. Drives the
    // bidirectional close reconciliation (BENG-4091).
    const shouldClosePopupRef = useRef(false);
    // A window we have asked to close and keep re-verifying until it is gone.
    const pendingCloseWindowRef = useRef(null);
    const { isExternalPaymentSystemEligible, isInlineNonTokenizableEnabled, isPaymentEligibilityLoading, isUsingExternalPaymentSystem, externalPaymentSystemMode, } = useIsExternalPaymentSystemEnabled();
    const { property: { model: propertyModel, payment, reservation }, environment: { language }, } = useStore();
    // Pending while the eligibility request is in flight or, on a direct /payment
    // load, before it has resolved (methods still null). Consumers use this to
    // avoid acting on the optimistic "external" default before the decision lands.
    const isPaymentRoutingPending = isPaymentEligibilityLoading ||
        (isExternalPaymentSystemEligible &&
            isInlineNonTokenizableEnabled &&
            payment.eligiblePaymentMethods === null);
    const propertyId = (propertyModel === null || propertyModel === void 0 ? void 0 : propertyModel.id) || 0;
    // Reused as the redirect target so a 3DS redirect navigates the open pop-up.
    const popupWindowName = `cb-bookingengine-${propertyId}-external-payment`;
    const paymentModal = useModal(ModalExternalPayment, {
        onCancel: (m) => {
            m.close();
            setIsProcessingPayment(false);
        },
    });
    const paymentInProgressModal = useModal(ModalPaymentInProgress, {
        onCancel: (m) => {
            var _a;
            m.close();
            setIsProcessingPayment(false);
            (_a = popupWindow.current) === null || _a === void 0 ? void 0 : _a.close();
        },
        width: 560,
    });
    const focusPopup = useCallback(() => {
        var _a;
        (_a = popupWindow.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [popupWindow]);
    // Closes the payment pop-up. A terminal outcome was reached, so flag it and
    // tell the window(s) to close themselves — a window closing itself is far more
    // reliable than the opener closing it (notably on mobile) — while also
    // attempting the direct close. The reconciliation interval and the pop-up's
    // heartbeat keep retrying until it is actually gone (BENG-4091). `source`
    // additionally closes the window that reported the outcome, in case the
    // tracked `popupWindow` ref went stale.
    const closePaymentPopup = (source) => {
        shouldClosePopupRef.current = true;
        const tracked = popupWindow.current;
        const reporting = asClosableWindow(source);
        const targets = reporting && reporting !== tracked ? [tracked, reporting] : [tracked];
        targets.forEach((win) => {
            var _a;
            if (!win)
                return;
            postMessage(win, 'cb-bookingengine-popup-close', {});
            (_a = win.close) === null || _a === void 0 ? void 0 : _a.call(win);
            pendingCloseWindowRef.current = win;
        });
        popupWindow.current = null;
    };
    const resetVerificationState = () => {
        awaitingThreeDsRef.current = false;
        pendingRedirectNextActionRef.current = null;
    };
    const checkPopupIsClosed = useDebouncedCallback(() => {
        var _a;
        if ((_a = popupWindow.current) === null || _a === void 0 ? void 0 : _a.closed) {
            popupWindow.current = null;
            // /prepare still running: keep the flow alive with the plain loading modal.
            if (isCreatingReservationRef.current) {
                paymentInProgressModal.open({ popupWindow: null });
                checkPopupIsClosed();
                return;
            }
            // Redirect verification pending: let the guest retry it.
            if (awaitingThreeDsRef.current && pendingRedirectNextActionRef.current) {
                awaitingThreeDsRef.current = false;
                openVerificationModal(pendingRedirectNextActionRef.current);
                checkPopupIsClosed();
                return;
            }
            // Nothing in progress: cancel.
            paymentInProgressModal.close();
            setIsProcessingPayment(false);
        }
        checkPopupIsClosed();
    }, 500);
    useEffect(() => {
        checkPopupIsClosed();
    }, [checkPopupIsClosed]);
    // Bidirectional close reconciliation (BENG-4091): every 5s keep trying to
    // close a window we have already decided to close, until it is actually gone.
    // Pairs with the pop-up's own 5s heartbeat so a missed close event on either
    // side is recovered on the next tick — failure-proof against lost events.
    useEffect(() => {
        const id = setInterval(() => {
            var _a;
            const pending = pendingCloseWindowRef.current;
            if (!pending)
                return;
            if (pending.closed) {
                pendingCloseWindowRef.current = null;
                return;
            }
            postMessage(pending, 'cb-bookingengine-popup-close', {});
            (_a = pending.close) === null || _a === void 0 ? void 0 : _a.call(pending);
        }, 5000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffectOnce(() => {
        return () => {
            var _a;
            paymentModal.close();
            paymentInProgressModal.close();
            (_a = popupWindow.current) === null || _a === void 0 ? void 0 : _a.close();
        };
    });
    const handleOpenSecurePaymentWindow = (popup) => {
        popupWindow.current = popup;
        // Fresh pop-up: it must stay open until a terminal outcome.
        shouldClosePopupRef.current = false;
        pendingCloseWindowRef.current = null;
        paymentInProgressModal.open({ popupWindow: popup });
    };
    const handleThreeDsWindowOpen = (popup) => {
        awaitingThreeDsRef.current = true;
        handleOpenSecurePaymentWindow(popup);
    };
    const openPaymentSystem = () => {
        var _a;
        scrollToTop();
        setIsProcessingPayment(true);
        if (externalPaymentSystemMode === 'modal') {
            return paymentModal.open();
        }
        if (externalPaymentSystemMode === 'popup') {
            // Close any lingering pop-up so the browser opens a fresh window instead
            // of reusing the old one under the same name.
            closePaymentPopup();
            resetVerificationState();
            const url = getExternalPaymentUrl(language, (_a = propertyModel === null || propertyModel === void 0 ? void 0 : propertyModel.code) !== null && _a !== void 0 ? _a : '');
            const { top, left, width, height } = getPopupWindowFeatures();
            const popup = window.open(url, popupWindowName, `width=${width}, height=${height}, top=${top}, left=${left}`);
            if (!popup) {
                setIsProcessingPayment(false);
                eventSystem.emit('internal/show-error-message', {
                    message: t('payments/external-payment-popup-blocked-message'),
                    title: t('payments/external-payment-popup-blocked-title'),
                });
                return;
            }
            handleOpenSecurePaymentWindow(popup);
            return;
        }
    };
    const closePaymentSystem = (source) => {
        setIsProcessingPayment(false);
        resetVerificationState();
        paymentModal.close();
        paymentInProgressModal.close();
        closePaymentPopup(source);
    };
    // Fetches the eligible methods into the store (shared source for the decision
    // and routing); returns null on failure so callers fall back to the popup.
    const resolveEligibleMethods = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        payment.setResolvingPaymentRouting(true);
        try {
            const methods = yield fetchEligiblePaymentMethods();
            payment.setEligiblePaymentMethods(methods);
            return methods;
        }
        catch (_a) {
            payment.setEligiblePaymentMethods(null);
            return null;
        }
        finally {
            payment.setResolvingPaymentRouting(false);
        }
    }), [payment]);
    // Payment-step entry point: the caller supplies what to do for each outcome.
    // Inline when the external system is off or the resolved methods are only
    // non-tokenizable; popup otherwise. The /settings request fires here.
    const completeCheckoutStep = useCallback((actions) => __awaiter(void 0, void 0, void 0, function* () {
        if (isCompletingCheckoutStepRef.current) {
            return;
        }
        isCompletingCheckoutStepRef.current = true;
        try {
            if (!isExternalPaymentSystemEligible) {
                return actions.onContinueInline();
            }
            if (!isInlineNonTokenizableEnabled) {
                return actions.onProceedExternal();
            }
            const methods = yield resolveEligibleMethods();
            if (isResolvedNonTokenizableOnly(methods)) {
                actions.onContinueInline();
            }
            else {
                actions.onProceedExternal();
            }
        }
        finally {
            isCompletingCheckoutStepRef.current = false;
        }
    }), [
        isExternalPaymentSystemEligible,
        isInlineNonTokenizableEnabled,
        resolveEligibleMethods,
    ]);
    // Resolves the decision on a direct load / refresh of `/payment` (it normally
    // resolves from the Guest "Continue"). No-op when it doesn't apply or is done.
    const ensurePaymentRoutingResolved = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (isExternalPaymentSystemEligible &&
            isInlineNonTokenizableEnabled &&
            payment.eligiblePaymentMethods === null) {
            yield resolveEligibleMethods();
        }
    }), [
        isExternalPaymentSystemEligible,
        isInlineNonTokenizableEnabled,
        payment,
        resolveEligibleMethods,
    ]);
    const handlePaymentError = (error) => {
        const parsedErrorCode = Number(error);
        const isValidNumeric = !Number.isNaN(parsedErrorCode);
        eventSystem.emit('internal/payment-error', Object.assign(Object.assign({}, (isValidNumeric ? { errorCode: parsedErrorCode } : { error })), { isFromExternalPaymentSystem: true }));
        onPaymentError === null || onPaymentError === void 0 ? void 0 : onPaymentError(error);
    };
    const handlePaymentSuccess = (_a) => __awaiter(void 0, [_a], void 0, function* ({ enc_res_id, }) {
        // @TECHDEBT: This should be handled in the confirmation page
        yield reservation.get(enc_res_id);
        if (!reservation.model || !reservation.reservationCode) {
            throw new Error('Error fetching reservation data');
        }
        reservation.handleAfterCreateReservation({
            enc_res_id,
            reservation_id: reservation.model.reservationId,
            status: reservation.model.status,
        });
        onCompleteReservationProcess === null || onCompleteReservationProcess === void 0 ? void 0 : onCompleteReservationProcess({ enc_res_id });
        navigate.goNext({
            data_res: enc_res_id,
        });
    });
    // handlePaymentSuccess can reject (network error / missing reservation data);
    // surface it instead of leaving the guest on a closed modal.
    const finishWithSuccess = (enc_res_id) => handlePaymentSuccess({ enc_res_id }).catch((e) => {
        setIsProcessingPayment(false);
        handlePaymentError(e instanceof Error ? e.message : 'Error fetching reservation data');
    });
    const failPaymentInMainWindow = (error) => {
        resetVerificationState();
        closePaymentPopup();
        paymentInProgressModal.close();
        setIsProcessingPayment(false);
        handlePaymentError(error);
    };
    // In-progress modal with a "verify payment" button; the redirect opens from
    // the user's click. Used when there is no open window to reuse for it.
    const openVerificationModal = (nextAction) => {
        pendingRedirectNextActionRef.current = nextAction;
        const { top, left, width, height } = getPopupWindowFeatures();
        paymentInProgressModal.open({
            nextAction,
            onErrorOpeningWindow: () => handlePaymentError(t('payments/external-payment-popup-blocked-message')),
            onOpenExternalWindow: handleThreeDsWindowOpen,
            target: '_blank',
            windowFeatures: `width=${width}, height=${height}, top=${top}, left=${left}`,
        });
    };
    // Finishes the checkout in the main window once /prepare returns (the
    // non-SDK-driven cases). The pop-up is reused for a redirect and closed on a
    // final outcome.
    const completeReservationInMainWindow = (reservation, source) => {
        var _a, _b;
        if (!reservation.status || reservation.error) {
            failPaymentInMainWindow((_a = reservation.error) !== null && _a !== void 0 ? _a : String((_b = reservation.errorCode) !== null && _b !== void 0 ? _b : ''));
            return;
        }
        const nextAction = createNextAction(reservation, ({ enc_res_id }) => finishWithSuccess(enc_res_id));
        if ((nextAction === null || nextAction === void 0 ? void 0 : nextAction.type) === 'redirect') {
            // Pop-up still open: reuse it for the acquirer redirect. It boots back
            // with `external-window-boot` and finishes the flow in the main window.
            if (popupWindow.current && !popupWindow.current.closed) {
                pendingRedirectNextActionRef.current = nextAction;
                awaitingThreeDsRef.current = true;
                processNextActionRedirect({
                    nextAction,
                    onErrorOpeningWindow: () => failPaymentInMainWindow(t('payments/external-payment-popup-blocked-message')),
                    target: popupWindowName,
                });
                return;
            }
            // Pop-up already closed: let the guest reopen the redirect from a click.
            openVerificationModal(nextAction);
            return;
        }
        if (!nextAction || nextAction.type === 'done') {
            closePaymentPopup(source);
            paymentInProgressModal.close();
            finishWithSuccess(reservation.enc_res_id);
            return;
        }
        failPaymentInMainWindow('unsupported-next-action');
    };
    // Next-actions other than `done`/`redirect` (e.g. PayPal) are owned by the SDK
    // inside the pop-up — hand the result back so the SDK can finish them.
    const isSdkDrivenNextAction = (reservation) => {
        var _a;
        const type = (_a = reservation.nextAction) === null || _a === void 0 ? void 0 : _a.type;
        return !!type && type !== 'done' && type !== 'redirect';
    };
    const finishPopupReservation = (source, requestId, reservation) => {
        if (isSdkDrivenNextAction(reservation)) {
            postMessage(source, 'cb-bookingengine-reservation-result', {
                requestId,
                reservation,
            });
            return;
        }
        completeReservationInMainWindow(reservation, source);
    };
    // Runs /prepare on behalf of the pop-up/iframe. Living in the main window
    // lets us cache and reuse the response across pop-up reopen (BENG-3933).
    const handleReservationRequest = (source, requestId, payment) => __awaiter(void 0, void 0, void 0, function* () {
        const isPopupMode = externalPaymentSystemMode === 'popup';
        // Only accept requests from our own pop-up, so no other frame can trigger
        // /prepare (and a deposit).
        if (isPopupMode && source !== popupWindow.current) {
            return;
        }
        // Repeated request after a successful /prepare: reuse the cached reservation.
        if (reservationResultRef.current) {
            if (isPopupMode) {
                finishPopupReservation(source, requestId, reservationResultRef.current);
            }
            else {
                postMessage(source, 'cb-bookingengine-reservation-result', {
                    requestId,
                    reservation: reservationResultRef.current,
                });
            }
            return;
        }
        // A /prepare is already in flight: do not start a second one (would
        // duplicate the reservation). Ignore in pop-up mode; reply to the iframe.
        if (isCreatingReservationRef.current) {
            if (!isPopupMode) {
                postMessage(source, 'cb-bookingengine-reservation-result', {
                    error: 'Reservation creation already in progress',
                    requestId,
                });
            }
            return;
        }
        isCreatingReservationRef.current = true;
        try {
            const { reservationRequest } = getExternalPaymentSystemProps(appParams, externalPaymentSystemMode);
            const reservation = yield ReservationService.makeReservation(Object.assign(Object.assign({}, reservationRequest), { payment: Object.assign(Object.assign({}, reservationRequest.payment), payment) }));
            // Cache only on success so a declined card can still be retried.
            if (reservation.status && !reservation.error) {
                reservationResultRef.current = reservation;
            }
            if (isPopupMode) {
                finishPopupReservation(source, requestId, reservation);
            }
            else {
                postMessage(source, 'cb-bookingengine-reservation-result', {
                    requestId,
                    reservation,
                });
            }
        }
        catch (e) {
            const message = e instanceof Error ? e.message : 'Error making reservation';
            if (isPopupMode) {
                failPaymentInMainWindow(message);
            }
            else {
                postMessage(source, 'cb-bookingengine-reservation-result', {
                    error: message,
                    requestId,
                });
            }
        }
        finally {
            isCreatingReservationRef.current = false;
        }
    });
    const { postMessage } = useCrossWindowMessage({
        onMessage: (message) => {
            var _a;
            if (message.type === 'cb-bookingengine-payment-page-loaded') {
                return postMessage(message.source, 'cb-bookingengine-payment-page-init-data', getExternalPaymentSystemProps(appParams, externalPaymentSystemMode));
            }
            if (message.type === 'cb-bookingengine-reservation-request') {
                return handleReservationRequest(message.source, message.payload.requestId, message.payload.payment);
            }
            // Pop-up heartbeat: answer whether it should close. Recovers a missed
            // close event since the pop-up keeps asking every 5s (BENG-4091).
            if (message.type === 'cb-bookingengine-popup-reconcile') {
                if (shouldClosePopupRef.current) {
                    postMessage(message.source, 'cb-bookingengine-popup-close', {});
                    (_a = asClosableWindow(message.source)) === null || _a === void 0 ? void 0 : _a.close();
                }
                return;
            }
            if (message.type === 'cb-bookingengine-payment-error') {
                closePaymentSystem(message.source);
                return handlePaymentError(message.payload.error);
            }
            if (message.type === 'cb-bookingengine-payment-success') {
                closePaymentSystem(message.source);
                return handlePaymentSuccess(message.payload);
            }
            if (message.type === 'cb-bookingengine-external-window-boot') {
                const { searchParams } = new URL(message.payload.url);
                const enc_res_id = searchParams.get('data_res');
                const error = searchParams.get('error_code');
                // Acquirer returned to the app: close the redirect pop-up and finish.
                if (error) {
                    closePaymentSystem(message.source);
                    handlePaymentError(error);
                }
                else if (enc_res_id) {
                    closePaymentSystem(message.source);
                    handlePaymentSuccess({ enc_res_id });
                }
            }
        },
    });
    return {
        closePaymentSystem,
        completeCheckoutStep,
        ensurePaymentRoutingResolved,
        externalPaymentSystemMode,
        focusPopup,
        handleOpenSecurePaymentWindow,
        isPaymentEligibilityLoading,
        isPaymentRoutingPending,
        isProcessingPayment,
        isUsingExternalPaymentSystem,
        openPaymentSystem,
    };
};
